import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { APIS } from '../../../config/apis';
import { ALERT_TYPE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { ServerRequest } from '../../../utils/apiweb';
import { TIPO_DOCUMENTO } from '../../../consts/entityType';

import { FormError } from '../../common';
import { useFormErrors } from '../../hooks';

import './index.scss';

const ArcaLookup = ({ tipoPersonaEsperado, onPersonaFound, onSkip }) => {

    const [numero, setNumero] = useState('');
    const [loading, setLoading] = useState(false);
    const [cuits, setCuits] = useState([]);
    const [showCuitSelect, setShowCuitSelect] = useState(false);
    const [aviso, setAviso] = useState(null);
    const [arcaFailed, setArcaFailed] = useState(false);

    const isCuit = (value) => /^\d{11}$/.test(value);
    const isDni = (value) => /^\d{7,8}$/.test(value);
    const detectarTipo = (value) => isCuit(value) ? 'CUIT' : 'DNI';
    const formatCuit = (cuit) => `${cuit.substring(0,2)}-${cuit.substring(2,10)}-${cuit.substring(10)}`;

    const getErrors = () => {
        const errors = {};

        if (!isCuit(numero) && !isDni(numero)) errors.numero = 'Ingresá un CUIT de 11 dígitos o un DNI de 7 u 8';

        return errors;
    };

    const { errorOf, validate } = useFormErrors(getErrors);

    // Mensaje al operador según el motivo que devuelve el backend (businessCode). Todos los casos
    // son no bloqueantes: el operador puede "Omitir y cargar manualmente".
    const mensajeErrorArca = (data) => {
        switch (data?.businessCode) {
            case 'ARCA_CUIT_INACTIVO':
                return 'El CUIT figura inactivo en AFIP. Podés cargar los datos manualmente.';
            case 'ARCA_NO_DISPONIBLE':
                return 'ARCA no está disponible en este momento. Podés cargar los datos manualmente.';
            default:
                return data?.message || 'No se pudo consultar ARCA. Podés continuar sin autocompletar.';
        }
    };

    const verificarYContinuar = (persona) => {
        if (persona.tipoPersona !== tipoPersonaEsperado) {
            const nombre = persona.tipoPersona === 'JURIDICA'
                ? persona.razonSocial
                : `${persona.nombre} ${persona.apellido}`.trim();
            const tipoLabel = persona.tipoPersona === 'JURIDICA' ? 'jurídica' : 'física';
            ShowToastMessage(ALERT_TYPE.ALERT_WARNING,
                `El CUIT ${persona.cuit} corresponde a una persona ${tipoLabel} (${nombre}). Debe crearla desde el formulario de persona ${tipoLabel}.`);
            setLoading(false);
            return;
        }

        if (persona.estadoClave && persona.estadoClave !== 'ACTIVO') {
            ShowToastMessage(ALERT_TYPE.ALERT_WARNING,
                `La persona figura como "${persona.estadoClave}" en ARCA. Verifique antes de continuar.`);
        }

        // PF usa check-documento-duplicado (cross-match DNI/CUIT); PJ usa filter por CUIT exacto
        // para evitar falsos positivos por la extracción de DNI desde el CUIT
        const checkUrl = tipoPersonaEsperado === 'FISICA'
            ? { base: APIS.URLS.PERSONA_FISICA, path: `/check-documento-duplicado?idTipoDocumento=${TIPO_DOCUMENTO.CUIT}&numeroDocumento=${encodeURIComponent(persona.cuit)}` }
            : { base: APIS.URLS.PERSONA_JURIDICA, path: `/filter?idTipoDocumento=${TIPO_DOCUMENTO.CUIT}&numeroDocumento=${encodeURIComponent(persona.cuit)}` };

        ServerRequest(
            REQUEST_METHOD.GET, null, true,
            checkUrl.base, checkUrl.path, null,
            (response) => {
                response.json().then(data => {
                    // PF devuelve { duplicado: true/false }, PJ devuelve array de resultados
                    const esDuplicado = tipoPersonaEsperado === 'FISICA'
                        ? data.duplicado
                        : (Array.isArray(data) && data.length > 0);
                    if (esDuplicado) {
                        const nombre = persona.razonSocial || `${persona.nombre} ${persona.apellido}`.trim();
                        setAviso({
                            mensaje: <>Ya existe una persona con CUIT <strong>{persona.cuit}</strong> ({nombre}).</>
                        });
                        setLoading(false);
                    } else {
                        onPersonaFound(persona);
                    }
                });
            },
            () => {
                ShowToastMessage(ALERT_TYPE.ALERT_WARNING, 'No se pudo verificar si el CUIT ya existe. Verifique manualmente.');
                onPersonaFound(persona);
            },
            () => {
                ShowToastMessage(ALERT_TYPE.ALERT_WARNING, 'No se pudo verificar si el CUIT ya existe. Verifique manualmente.');
                onPersonaFound(persona);
            }
        );
    };

    const handleNumeroChange = (e) => {
        setNumero(e.target.value.replace(/\D/g, ''));
    };

    const buscar = () => {
        if (!validate()) return;

        setLoading(true);
        setAviso(null);
        setCuits([]);
        setShowCuitSelect(false);

        ServerRequest(
            REQUEST_METHOD.POST, null, true,
            APIS.URLS.ARCA, '/consultar-persona',
            { tipo: detectarTipo(numero), numero },
            (response) => {
                response.json().then(data => {
                    if (!data.encontrado) {
                        setLoading(false);
                        // ARCA respondió OK pero sin datos: habilitar Omitir para que el usuario pueda continuar manual
                        setArcaFailed(true);
                        ShowToastMessage(ALERT_TYPE.ALERT_WARNING, 'No se encontraron datos en ARCA para ese documento');
                        return;
                    }
                    setArcaFailed(false);
                    if (data.persona) {
                        verificarYContinuar(data.persona);
                    } else if (data.cuits && data.cuits.length > 1) {
                        setLoading(false);
                        setCuits(data.cuits);
                        setShowCuitSelect(true);
                    } else if (data.cuits && data.cuits.length === 1) {
                        seleccionarCuit(data.cuits[0]);
                    }
                }).catch(err => {
                    setLoading(false);
                    setArcaFailed(true);
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Respuesta de ARCA inválida');
                    console.error('[ARCA] respuesta no parseable:', err);
                });
            },
            (response) => {
                setLoading(false);
                // El frontend valida el formato antes de enviar; un no-2xx del backend implica
                // que ARCA no resolvió (CUIT inactivo / servicio no disponible): habilitar Omitir.
                setArcaFailed(true);
                response.json().then(data => {
                    ShowToastMessage(ALERT_TYPE.ALERT_WARNING, mensajeErrorArca(data));
                }).catch(() => {
                    ShowToastMessage(ALERT_TYPE.ALERT_WARNING, 'No se pudo consultar ARCA. Podés continuar sin autocompletar.');
                });
            },
            (error) => {
                setLoading(false);
                setArcaFailed(true);
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error consultando ARCA. Puede continuar sin autocompletar.');
                console.error('[ARCA] consultar-persona:', error);
            }
        );
    };

    const seleccionarCuit = (cuit) => {
        setShowCuitSelect(false);
        setNumero(cuit);
        setLoading(true);

        ServerRequest(
            REQUEST_METHOD.POST, null, true,
            APIS.URLS.ARCA, '/consultar-persona',
            { tipo: 'CUIT', numero: cuit },
            (response) => {
                response.json().then(data => {
                    if (data.encontrado && data.persona) {
                        setArcaFailed(false);
                        verificarYContinuar(data.persona);
                    } else {
                        setLoading(false);
                        setArcaFailed(true);
                        ShowToastMessage(ALERT_TYPE.ALERT_WARNING, 'No se encontraron datos para el CUIT seleccionado');
                    }
                }).catch(err => {
                    setLoading(false);
                    setArcaFailed(true);
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Respuesta de ARCA inválida');
                    console.error('[ARCA] respuesta no parseable:', err);
                });
            },
            (response) => {
                setLoading(false);
                setArcaFailed(true);
                response.json().then(data => {
                    ShowToastMessage(ALERT_TYPE.ALERT_WARNING, mensajeErrorArca(data));
                }).catch(() => {
                    ShowToastMessage(ALERT_TYPE.ALERT_WARNING, 'No se pudo consultar ARCA. Podés continuar sin autocompletar.');
                });
            },
            () => {
                setLoading(false);
                setArcaFailed(true);
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error consultando ARCA');
            }
        );
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') buscar();
    };

    return (
        <div className="arca-lookup">
            <div className="arca-lookup-card">
                <div className="arca-lookup-header">
                    <span className="material-symbols-outlined">search</span>
                    <h4>Buscar persona en ARCA</h4>
                </div>
                <p className="arca-lookup-description">
                    Ingrese el CUIT o DNI para autocompletar los datos desde ARCA (ex AFIP).
                </p>
                <div className="arca-lookup-input-row">
                    <input
                        type="text"
                        className={`form-control ${errorOf('numero') ? 'is-invalid' : ''}`}
                        placeholder="Ej: 20345678901 o 34567890"
                        aria-label="CUIT o DNI"
                        value={numero}
                        onChange={handleNumeroChange}
                        onKeyDown={handleKeyDown}
                        maxLength={11}
                        autoFocus
                        disabled={loading}
                    />
                    <button
                        className="btn btn-primary"
                        onClick={buscar}
                        disabled={loading || (!isCuit(numero) && !isDni(numero))}
                    >
                        {loading ? (
                            <span className="spinner-border spinner-border-sm" />
                        ) : (
                            'Buscar'
                        )}
                    </button>
                </div>
                <FormError message={errorOf('numero')} />

                {showCuitSelect && cuits.length > 0 && (
                    <div className="arca-lookup-cuit-select">
                        <p>Se encontraron múltiples CUITs para ese DNI. Seleccione uno:</p>
                        <div className="arca-lookup-cuit-list">
                            {cuits.map(cuit => (
                                <button
                                    key={cuit}
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={() => seleccionarCuit(cuit)}
                                >
                                    {formatCuit(cuit)}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {aviso && (
                    <div className="arca-lookup-aviso">
                        <span className="material-symbols-outlined">warning</span>
                        <p>{aviso.mensaje}</p>
                    </div>
                )}

                {arcaFailed && (
                    <div className="arca-lookup-footer">
                        <button className="btn btn-link" onClick={onSkip} disabled={loading}>
                            Omitir y cargar manualmente
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

ArcaLookup.propTypes = {
    tipoPersonaEsperado: PropTypes.oneOf(['FISICA', 'JURIDICA']).isRequired,
    onPersonaFound: PropTypes.func.isRequired,
    onSkip: PropTypes.func.isRequired,
};

export default ArcaLookup;
